import { Injectable } from '@angular/core';
import {
  DwAction,
  DwActionHandler,
  DwActionResult,
  DwActionType,
  DwEventService,
  DwGridActionResult,
  DwMetaDataGridActionState,
  DwModalButtonStandardConfigs,
  DwModalConfig,
  DwModalResult,
  DwModalService,
  DwModalSize,
  DwOrmDataService
} from '@devwareapps/devware-cap';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppMetaDataItemNames, AssignmentStudentEntity } from '../../../meta-data/app-meta-data.service';
import { AssignmentAddStudentsModalComponent } from '../components/assignment-add-students-modal/assignment-add-students-modal.component';
import { AddAssignmentStudentsResult } from '../models/assignment-student-management.model';

@DwAction({
  keyName: 'assignment-add-students-action',
  display: 'Add Students to Assignment',
  actionType: DwActionType.gridAction,
  parentItemName: AppMetaDataItemNames.AssignmentStudent,
  isGlobal: false,
  defaultButtonConfig: {
    useButtonDefaults: true,
    useParentDisplay: false,
    buttonText: 'Add Students',
    iconClass: 'fa fa-user-plus',
    buttonClass: 'btn btn-primary'
  },
  defaultSecurityConfig: {
    requireAuthentication: true,
    requireCanUpdate: true,
    useEmulatedPermissions: true
  }
})
@Injectable()
export class AssignmentAddStudentsActionService implements DwActionHandler {
  constructor(
    private dwModalService: DwModalService,
    private dwEventService: DwEventService
  ) { }

  handleAction(actionState: DwMetaDataGridActionState<AssignmentStudentEntity>): Observable<DwActionResult> {
    const assignmentId = Number(actionState.metaDataGridApi.addContext?.AssignmentId);
    if (!(assignmentId > 0)) {
      return of({
        success: false,
        message: 'The parent assignment could not be determined.',
        refreshData: false
      } as DwGridActionResult);
    }

    const modalConfig = new DwModalConfig<number>();
    modalConfig.title = 'Add Students to Assignment';
    modalConfig.data = assignmentId;
    modalConfig.component = AssignmentAddStudentsModalComponent;
    modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndClose;
    modalConfig.buttons[0].buttonText = 'Add Students';
    modalConfig.buttons[0].buttonClass = 'btn btn-primary';
    modalConfig.modalSize = DwModalSize.large;
    modalConfig.allowFullScreen = false;

    // Grid actions execute during the grid's change-detection pass. Opening the
    // modal synchronously can change CAP layout visibility before that pass ends.
    return timer(0).pipe(
      switchMap(() => this.dwModalService.showModal<number, AddAssignmentStudentsResult>(modalConfig)),
      map(modalResult => this.createActionResult(modalResult))
    );
  }

  private createActionResult(modalResult: DwModalResult<AddAssignmentStudentsResult>): DwGridActionResult {
    const result: DwGridActionResult = {
      success: !modalResult.cancelled,
      cancelled: modalResult.cancelled,
      refreshData: !modalResult.cancelled
    };

    if (modalResult.cancelled) {
      return result;
    }

    this.dwEventService.publishEvent(
      DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.AssignmentStudent), {});
    this.dwEventService.publishEvent(
      DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Assignment), {});

    const addedCount = modalResult.data?.AddedStudentIds?.length || 0;
    result.message = `${addedCount} ${addedCount === 1 ? 'student was' : 'students were'} added to the assignment.`;
    return result;
  }
}
